import { runBatch } from '../../../utils/office/document';

/**
 * Checks all REF fields in the document for errors.
 * Highlights broken references in red and returns the count.
 */
const REF_ERROR_HIGHLIGHT = 'Red';

/**
 * Checks REF *fields* for broken bookmarks (a complement to the textual
 * cross-reference check). Clears any prior red marks first so re-running after a
 * fix is clean, then re-flags the still-broken ones. Returns the error count.
 */
export async function checkCrossReferences(): Promise<number> {
  let errorCount = 0;

  await runBatch(async (context) => {
    const fields = context.document.body.fields;
    fields.load('items');
    await context.sync();

    for (const field of fields.items) field.load('code');
    await context.sync();

    const refs = fields.items.filter((f) => f.code && f.code.trimStart().startsWith('REF '));
    for (const field of refs) field.updateResult();
    await context.sync();

    for (const field of refs) field.result.load('text');
    await context.sync();

    // Word renders a broken reference as an error string (localized) — match the
    // known variants. NOT the substring 'REF' (that flagged valid refs whose
    // TARGET text contains "REF", e.g. "REFERENCES").
    const errorPatterns = [
      'Fehler!',
      'Error!',
      'Textmarke nicht definiert',
      'Bookmark not defined',
      "n'est pas défini", // FR
      'no está definido', // ES
    ];

    for (const field of refs) {
      // Always clear any prior mark, so a now-fixed ref stops being red.
      field.result.font.highlightColor = '';
      const resultText = field.result.text || '';
      // Broken if it matches an error string OR renders empty (deleted target).
      const hasError = resultText.trim() === '' || errorPatterns.some((p) => resultText.includes(p));
      if (hasError) {
        field.result.font.highlightColor = REF_ERROR_HIGHLIGHT;
        errorCount++;
      }
    }

    await context.sync();
  });

  return errorCount;
}

/** Normalizes a list number for tolerant comparison: strips a leading
 *  "§ "/"Ziffer "/"Abschnitt " prefix and any NBSP, and drops a trailing dot
 *  ("1.1." → "1.1", "§ 1" → "1"). */
export function normalizeListNumber(s: string): string {
  return s
    .replace(/\u00a0/g, ' ')
    .replace(/^\s*(?:§|Ziffer|Ziff\.|Abschnitt|Klausel|Artikel|Section|Clause)\s*/i, '')
    .replace(/[.)\]]\s*$/, '')
    .trim();
}

/** Every anchor this add-in creates starts with this. The leading underscore
 *  makes the bookmark HIDDEN — Word keeps `_`-prefixed bookmarks out of the
 *  Insert → Bookmark list, the same way its own `_Ref…`/`_Toc…` anchors are
 *  invisible. The `V14` marks ours among them. */
export const BOOKMARK_PREFIX = '_V14';

const BOOKMARK_ALPHABET = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
const BOOKMARK_RANDOM_LENGTH = 10;

/**
 * A fresh anchor name: `_V14` + 10 random letters/digits (e.g. `_V14a7Kd2Xp9Q`).
 *
 * Names used to be derived from the paragraph number (`_Ref_1_1`), which is
 * exactly what made them brittle: renumbering a contract — the normal case —
 * left the name pointing at a paragraph that no longer carries that number, and
 * two paragraphs that ever share a number share the bookmark. A random name
 * carries no meaning that can go stale; the REF field resolves the number at
 * display time anyway.
 *
 * Length and character set stay inside Word's limits (letters, digits and
 * underscore, max 40 chars) by construction.
 */
export function makeBookmarkName(): string {
  const bytes = new Uint8Array(BOOKMARK_RANDOM_LENGTH);
  const cryptoApi = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (cryptoApi?.getRandomValues) cryptoApi.getRandomValues(bytes);
  else for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);

  let name = BOOKMARK_PREFIX;
  for (const b of bytes) name += BOOKMARK_ALPHABET[b % BOOKMARK_ALPHABET.length];
  return name;
}

/**
 * The anchor to use at `targetRange`: an anchor of ours already sitting there is
 * reused, otherwise a new one is created. Keeps repeated references to the same
 * paragraph from stacking up bookmarks, without ever reusing one across
 * paragraphs. Falls back to a fresh name on hosts without `getBookmarks`.
 */
export async function anchorAt(
  context: Word.RequestContext,
  targetRange: Word.Range,
): Promise<string> {
  try {
    const existing = targetRange.getBookmarks(false, true);
    await context.sync();
    const ours = (existing.value ?? []).find((n) => n.startsWith(BOOKMARK_PREFIX));
    if (ours) return ours;
  } catch {
    /* getBookmarks unavailable (older host) — just make a new anchor */
  }
  const name = makeBookmarkName();
  targetRange.insertBookmark(name);
  return name;
}

/**
 * How far back to look for a numbered paragraph. Each hop is a round-trip, so an
 * unnumbered document must not walk the whole body — 200 covers "the cursor is a
 * few unnumbered paragraphs below its section heading", which is the real case.
 */
const MAX_NUMBER_LOOKBACK = 200;

/**
 * The paragraph whose number a reference inserted at `start` should use: `start`
 * itself when it is numbered, otherwise the closest PRECEDING numbered one.
 *
 * Shared by the self-reference and the exhibit, because both mean the same thing
 * by "the current section": in a contract the cursor is usually in an unnumbered
 * continuation paragraph, and the number that matters is the one above it.
 */
export async function findNumberSourceParagraph(
  context: Word.RequestContext,
  start: Word.Paragraph,
): Promise<{ paragraph: Word.Paragraph; listString: string } | null> {
  let candidate: Word.Paragraph | null = start;
  for (let hops = 0; candidate && hops < MAX_NUMBER_LOOKBACK; hops++) {
    const li = candidate.listItemOrNullObject;
    li.load('isNullObject,listString');
    const previous = candidate.getPreviousOrNullObject();
    previous.load('isNullObject');
    // eslint-disable-next-line no-await-in-loop -- each hop depends on the last
    await context.sync();
    const listString = li.isNullObject ? '' : (li.listString ?? '').trim();
    if (listString) return { paragraph: candidate, listString };
    candidate = previous.isNullObject ? null : previous;
  }
  return null;
}

/**
 * Inserts a cross-reference (REF field) to the paragraph matching
 * the given paragraph number string.
 */
export async function insertCrossReferenceByNumber(
  paragraphNumber: string,
): Promise<void> {
  await runBatch(async (context) => {
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load('items');
    await context.sync();

    // Load listItem for each paragraph (nullObject-safe).
    const listItems = paragraphs.items.map((p) => p.listItemOrNullObject);
    for (const li of listItems) li.load('isNullObject,listString');
    await context.sync();

    // Find the paragraph whose NORMALIZED list number matches (tolerates a
    // trailing dot "1.1." vs "1.1", NBSP, and a "§ "/"Ziffer " prefix the user
    // may have typed).
    const wanted = normalizeListNumber(paragraphNumber);
    let targetParagraph: Word.Paragraph | null = null;
    for (let i = 0; i < paragraphs.items.length; i++) {
      const li = listItems[i];
      if (!li.isNullObject && normalizeListNumber(li.listString ?? '') === wanted) {
        targetParagraph = paragraphs.items[i];
        break;
      }
    }

    if (!targetParagraph) {
      throw new Error(
        `Paragraph with number "${paragraphNumber}" not found.`,
      );
    }

    // Create/reuse our anchor at the target paragraph start
    const targetRange = targetParagraph.getRange(Word.RangeLocation.start);
    const bookmarkName = await anchorAt(context, targetRange);

    // Insert REF field at current selection
    const selection = context.document.getSelection();
    selection.insertField(
      Word.InsertLocation.replace,
      Word.FieldType.empty,
      `REF ${bookmarkName} \\h \\w`,
    );

    await context.sync();
  });
}

/**
 * Inserts a self-reference (REF field) to the number of the section the cursor is
 * in: the current paragraph when it is numbered, otherwise the closest PRECEDING
 * numbered one — the same rule the exhibit uses, because the cursor is usually in
 * an unnumbered continuation paragraph and the section number above it is the one
 * meant. Refuses only when there is no numbered paragraph at or before the
 * cursor, where the field would render blank.
 */
export async function insertSelfReference(): Promise<void> {
  await runBatch(async (context) => {
    const selection = context.document.getSelection();
    const paragraphs = selection.paragraphs;
    paragraphs.load('items');
    await context.sync();

    if (paragraphs.items.length === 0) {
      throw new Error('No paragraph found at the current position.');
    }

    const source = await findNumberSourceParagraph(context, paragraphs.items[0]);
    if (!source) {
      throw new Error(
        'No numbered section found at or before the cursor — a self-reference is not possible.',
      );
    }

    const paraStart = source.paragraph.getRange(Word.RangeLocation.start);
    const bookmarkName = await anchorAt(context, paraStart);

    selection.insertField(
      Word.InsertLocation.replace,
      Word.FieldType.empty,
      `REF ${bookmarkName} \\h \\w`,
    );

    await context.sync();
  });
}
